import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { apiService, isAuthenticated } from '../../api/apiService';
import { UserSelector } from '../UserSelection';
import { ROOT_PATHS, TEMPLATE_TYPE } from '../../Constants';
import { useAppSelector } from '../../redux/hooks';
import { selectFilteredUsers } from '../../redux/slices/global';
import AgLoadingCard from '../../stories/AgLoadingCard';
import { useIntl } from 'react-intl';
// lazy load the layout component causing some errors. Plus, it will always be in use, so we don't realy need to lazy load it.
import Layout from '../../components/Layout';
import AddStaff from '../StaffInfo/AddStaff/AddStaff';
import { OrgRouteElementWrapper } from '../OrganizationInfo';
import AgVersion from '../../components/AgVersion';
import AgErrorBoundary from '../../components/AgErrorBoundary';
import AddOrganization from '../OrganizationInfo/AddOrganization';
import AccountPage from '../Account';
import { StaffRouteElementWrapper } from '../StaffInfo';
import ConsentProvider from '../../components/ConsentProvider';
import BulkUploadRequest from '../BulkUploadRequest';
import BulkUploadRequestDetails from '../BulkUploadRequest/Details';
import OrgAssetTypes from '../OrganizationInfo/AssetTypes';
import OrgPaymentTypes from '../OrganizationInfo/PaymentTypes';

const Login = lazy(() => import('../Login'));
const AddMember = lazy(() => import('../MemberInfo/AddMember/AddMember'));
const PageNotFound = lazy(() => import('../../components/PageNotFound'));
const SignupRedirect = lazy(() => import('../Signup/SignupRedirect'));
const Signup = lazy(() => import('../Signup'));
const Home = lazy(() => import('../Home'));
const Organisations = lazy(() => import('../Organisations'));
const Organizations = lazy(() => import('../Organizations'));
const Users = lazy(() => import('../Users'));
const Administration = lazy(() => import('../Administration'));
const UserSelection = lazy(() => import('../UserSelection'));
const Search = lazy(() => import('../Search'));
const ListManagement = lazy(() => import('../ListManagement'));
const Certificates = lazy(() => import('../Certificates'));
const EditCertificate = lazy(() => import('../Certificates/EditCertificate'));
const AddCertification = lazy(() => import('../Certificates/AddCertificate'));
const UserGuidance = lazy(() => import('../UserGuidance'));
const AssetTypes = lazy(() => import('../AssetTypes'));
const CustomFields = lazy(() => import('../CustomFields'));
const CustomFieldsDetails = lazy(() => import('../CustomFields/Details'));
const ResetPassword = lazy(() => import('../ResetPassword'));
const StartBulkImport = lazy(() => import('../StartBulkImport'));
const NewUserOrg = lazy(() => import('../NewUserOrg'));
const Localisation = lazy(() => import('../Localisation'));
const Surveys = lazy(() => import('../Surveys'));
const SurveyResult = lazy(() => import('../SurveyResult'));
const MicroSoftSsoRedirect = lazy(
  () => import('../Login/MicroSoftSSO/MicroSoftSSORedirect')
);
const ForgotPassword = lazy(() => import('../ForgotPassword'));
const AccountBlock = lazy(() => import('../AccountBlock'));
const SystemNotAvailable = lazy(
  () => import('../../components/SystemNotAvailable')
);
const NoLicense = lazy(() => import('../NoLicense'));
const Members = lazy(() => import('../Members'));
const BulkUploadTemplates = lazy(
  () => import('../BulkUpload/BulkUploadTemplates')
);
const BulkUpload = lazy(() => import('../BulkUpload/BulkUpload'));
const MembersWithOrgId = lazy(() => import('../Members/MembersWithOrgId'));

const Staff = lazy(() => import('../Staff'));

const Help = lazy(() => import('../Help'));
const MemberInfo = lazy(() => import('../MemberInfo'));
const Reports = lazy(() => import('../Reports'));

const Licenses = lazy(() => import('../Licenses'));
const LicenseDetails = lazy(() => import('../Licenses/Details'));

const Enums = lazy(() => import('../Enums'));
const EnumDetails = lazy(() => import('../Enums/Details'));

const AssetTypeList = lazy(() => import('../AssetTypeList'));
const AssetTypeDetails = lazy(() => import('../AssetTypeList/Details'));

const Countries = lazy(() => import('../Countries'));

const Units = lazy(() => import('../Units'));
const UnitDetails = lazy(() => import('../Units/Details'));

const Cultures = lazy(() => import('../Cultures'));

const Consents = lazy(() => import('../Consents'));
const ConsentDetails = lazy(() => import('../Consents/Details'));

const ContractTypes = lazy(() => import('../Contracts'));
const ContractTypeDetails = lazy(() => import('../Contracts/Details'));

const TrainingTopics = lazy(() => import('../TrainingTopics'));
const TrainingTopic = lazy(() => import('../TrainingTopics/Details'));

const FunctionsList = lazy(() => import('../Functions'));

const TrainingsSession = lazy(() => import('../Trainings'));
const TrainingDetailsSession = lazy(() => import('../Trainings/Details'));

const CertificateTypes = lazy(() => import('../CertificateTypes'));
const CertificateTypesDetails = lazy(
  () => import('../CertificateTypes/Details')
);

const Logs = lazy(() => import('../Logs'));

const OnHand = lazy(() => import('../Inventory/OnHand'));
const History = lazy(() => import('../Inventory/History'));
const Processing = lazy(() => import('../Inventory/Processing'));
const UploadSection = lazy(() => import('../Inventory/UploadSection'));
const BatchDetails = lazy(() => import('../Inventory/BatchDetails'));
const TransactionDetails = lazy(
  () => import('../Inventory/TransactionDetails')
);
const BulkPurchase = lazy(() => import('../Inventory/BulkPurchase'));
const BulkPurchaseDetails = lazy(
  () => import('../Inventory/BulkPurchase/Details')
);
const TransactionReport = lazy(() => import('../Inventory/TransactionReport'));
const PaymentManager = lazy(() => import('../PaymentManager'));

const Router = () => {
  const users = useAppSelector(selectFilteredUsers);
  const messages = useIntl().formatMessage;
  const location = useLocation();

  if (!isAuthenticated() || (!apiService.userToken && !users?.length)) {
    // if not authenticated or if no user token and no users exist
    // If the location pathname is not the base path
    if (location.hash.startsWith(ROOT_PATHS.ACCESS_TOKEN)) {
      // url for Single sign on has url starts with access_token
      return (
        // todo replace fallback when new deisng page handed over
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <Routes>
            <Route path='*' element={<MicroSoftSsoRedirect />} />
          </Routes>
        </Suspense>
      );
    }
    if (location.pathname.startsWith(ROOT_PATHS.RESET_PW)) {
      // url for Single sign on has url starts with access_token
      return (
        // todo replace fallback when new deisng page handed over
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <AgVersion fixed />
          <Routes>
            <Route path='*' element={<ResetPassword />} />
          </Routes>
        </Suspense>
      );
    }
    if (location.pathname === ROOT_PATHS.ROOT) {
      return (
        // todo replace fallback when new deisng page handed over
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <Routes>
            <Route path='*' element={<Navigate to={ROOT_PATHS.LOGIN} />} />
          </Routes>
        </Suspense>
      );
    }
    return (
      // todo replace fallback when new deisng page handed over
      <Suspense
        fallback={
          <AgLoadingCard
            text={messages({ id: 'LOADING' })}
            style={{ position: 'relative', height: '100vh' }}
            showLogo
          />
        }
      >
        <AgVersion fixed />
        <Routes>
          <Route path={ROOT_PATHS.LOGIN} element={<Login />} />
          <Route
            path={ROOT_PATHS.SYSTEM_UNAVAILABLE}
            element={<SystemNotAvailable />}
          />
          <Route path={ROOT_PATHS.SIGN_UP} element={<Signup />} />
          <Route
            path={ROOT_PATHS.SIGN_UP + '/:token'}
            element={<SignupRedirect />}
          />
          <Route
            path={ROOT_PATHS.USER_SELECT}
            element={<Navigate to='ROOT_PATHS.LOGIN' />}
          />
          <Route path={ROOT_PATHS.ACCOUNT_BLOCK} element={<AccountBlock />} />
          <Route path={ROOT_PATHS.FORGOT_PW} element={<ForgotPassword />} />
          <Route path={ROOT_PATHS.E404} element={<PageNotFound />} />
          <Route path='*' element={<Navigate to={ROOT_PATHS.LOGIN} />} />
        </Routes>
      </Suspense>
    );
  }

  if (!apiService.userToken) {
    // where should be users existing
    return (
      // todo replace fallback when new deisng page handed over
      <Suspense
        fallback={
          <AgLoadingCard
            text={messages({ id: 'LOADING' })}
            style={{ position: 'relative', height: '100vh' }}
            showLogo
          />
        }
      >
        <AgVersion fixed />
        <Routes>
          <Route path={ROOT_PATHS.USER_SELECT} element={<UserSelector />} />
          <Route
            path={ROOT_PATHS.RESET_PW + '/:token'}
            element={<ResetPassword />}
          />
          <Route path={ROOT_PATHS.E404} element={<PageNotFound />} />
          <Route
            path={ROOT_PATHS.SYSTEM_UNAVAILABLE}
            element={<SystemNotAvailable />}
          />
          <Route path='*' element={<Navigate to={ROOT_PATHS.USER_SELECT} />} />
        </Routes>
      </Suspense>
    );
  }

  switch (window.location.pathname) {
    case ROOT_PATHS.RESET_PW: {
      return (
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <AgVersion fixed />
          <Routes>
            <Route path='*' element={<ResetPassword />} />
          </Routes>
        </Suspense>
      );
    }

    case ROOT_PATHS.E404: {
      //the 404 page occupies whole screen
      return (
        // todo replace fallback when new deisng page handed over
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <AgVersion fixed />
          <Routes>
            <Route path={ROOT_PATHS.E404} element={<PageNotFound />} />
          </Routes>
        </Suspense>
      );
    }

    case ROOT_PATHS.NO_LICENSE: {
      return (
        <Suspense
          fallback={
            <AgLoadingCard
              text={messages({ id: 'LOADING' })}
              style={{ position: 'relative', height: '100vh' }}
              showLogo
            />
          }
        >
          <AgVersion fixed />
          <Routes>
            <Route path={ROOT_PATHS.NO_LICENSE} element={<NoLicense />} />
          </Routes>
        </Suspense>
      );
    }
  }

  if (window.location.pathname === ROOT_PATHS.SYSTEM_UNAVAILABLE)
    return <SystemNotAvailable />;

  return (
    // todo replace fallback when new deisng page handed over
    <Suspense
      fallback={
        <AgLoadingCard
          text={messages({ id: 'LOADING' })}
          style={{ position: 'relative', height: '100vh' }}
          showLogo
        />
      }
    >
      <Layout>
        <AgErrorBoundary>
          <ConsentProvider>
            <Routes>
              <Route
                path={ROOT_PATHS.USER_SELECT}
                element={<UserSelection />}
              />
              <Route
                path={ROOT_PATHS.RESET_PW + '/:token'}
                element={<ResetPassword />}
              />

              <Route path={ROOT_PATHS.ROOT} element={<Home />} />
              <Route path={ROOT_PATHS.HELP} element={<Help />} />
              <Route
                path={ROOT_PATHS.ORGANISATIONS}
                element={<Organizations />}
              />
              <Route
                path={ROOT_PATHS.ORGANISATION + '/:orgId'}
                element={<OrgRouteElementWrapper />}
              />
              <Route
                path={ROOT_PATHS.ADD_ORGANISATION}
                element={<AddOrganization />}
              />
              <Route
                path={
                  ROOT_PATHS.ORGANISATIONS +
                  '/:org_id' +
                  ROOT_PATHS.CHILD_ORGANISATIONS +
                  '/:childOrgId'
                }
                element={<Organisations />}
              />
              <Route path={ROOT_PATHS.USERS} element={<Users />} />
              <Route
                path={ROOT_PATHS.USERS + '/:user_id'}
                element={<Users />}
              />
              <Route
                path={ROOT_PATHS.NEW_USER_ORG + '/:user_id'}
                element={<NewUserOrg />}
              />

              <Route
                path={ROOT_PATHS.CUSTOM_FIELDS}
                element={<CustomFields />}
              />
              <Route
                path={ROOT_PATHS.CUSTOM_FIELDS + '/:id'}
                element={<CustomFieldsDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_CUSTOM_FIELDS}
                element={<CustomFieldsDetails isAdd />}
              />

              <Route
                path={ROOT_PATHS.ADMINISTRATION}
                element={<Administration />}
              />

              <Route
                path={ROOT_PATHS.LIST_MANAGEMENT}
                element={<ListManagement />}
              />
              <Route
                path={ROOT_PATHS.ACCOUNT + '/*'}
                element={<AccountPage />}
              />

              <Route
                path={ROOT_PATHS.CERTIFICATES}
                element={<Certificates />}
              />
              <Route
                path={ROOT_PATHS.CERTIFICATE + '/:id'}
                element={<EditCertificate />}
              />
              <Route
                path={ROOT_PATHS.ADD_CERTIFICATE}
                element={<AddCertification />}
              />
              <Route
                path={ROOT_PATHS.USER_GUIDANCE}
                element={<UserGuidance />}
              />
              <Route path={ROOT_PATHS.ASSET_TYPES} element={<AssetTypes />} />
              <Route
                path={ROOT_PATHS.LOCALISATION}
                element={<Localisation />}
              />
              <Route path={ROOT_PATHS.SURVEYS} element={<Surveys />} />
              <Route
                path={ROOT_PATHS.SURVEYS + '/result/:surveyId'}
                element={<SurveyResult />}
              />

              <Route path={ROOT_PATHS.LICENSES} element={<Licenses />} />
              <Route
                path={ROOT_PATHS.LICENSES + '/:id'}
                element={<LicenseDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_LICENSE}
                element={<LicenseDetails isAdd />}
              />

              <Route path={ROOT_PATHS.ENUMS} element={<Enums />} />
              <Route
                path={ROOT_PATHS.ENUMS + '/:id'}
                element={<EnumDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_ENUM}
                element={<EnumDetails isAdd />}
              />

              <Route
                path={ROOT_PATHS.ASSET_TYPE_LIST}
                element={<AssetTypeList />}
              />
              <Route
                path={ROOT_PATHS.ASSET_TYPE_LIST + '/:id'}
                element={<AssetTypeDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_ASSET_TYPE_LIST}
                element={<AssetTypeDetails isAdd />}
              />

              <Route path={ROOT_PATHS.COUNTRIES} element={<Countries />} />

              <Route path={ROOT_PATHS.UNITS} element={<Units />} />
              <Route
                path={ROOT_PATHS.UNITS + '/:id'}
                element={<UnitDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_UNIT}
                element={<UnitDetails isAdd />}
              />

              <Route path={ROOT_PATHS.CULTURES} element={<Cultures />} />

              <Route path={ROOT_PATHS.CONSENTS} element={<Consents />} />
              <Route
                path={ROOT_PATHS.CONSENTS + '/:id'}
                element={<ConsentDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_CONSENT}
                element={<ConsentDetails isAdd />}
              />

              <Route
                path={ROOT_PATHS.CONTRACT_TYPES}
                element={<ContractTypes />}
              />
              <Route
                path={ROOT_PATHS.CONTRACT_TYPES + '/:id'}
                element={<ContractTypeDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_CONTRACT_TYPE}
                element={<ContractTypeDetails isAdd />}
              />

              <Route
                path={ROOT_PATHS.SEARCH}
                element={<Search template={TEMPLATE_TYPE.Member} />}
              />
              <Route
                path={ROOT_PATHS.SEARCH + '/:searchTerm'}
                element={<Search template={TEMPLATE_TYPE.Member} />}
              />

              <Route path={ROOT_PATHS.MEMBERS} element={<Members />} />
              <Route
                path={ROOT_PATHS.MEMBERS + '/:orgId'}
                element={<MembersWithOrgId />}
              />
              <Route
                path={ROOT_PATHS.BULK_UPLOAD_TEMPLATES}
                element={<BulkUploadTemplates />}
              />
              <Route
                path={`${ROOT_PATHS.BULK_UPLOAD}/:orgId/:uploadKey?`}
                element={<BulkUpload />}
              />
              <Route
                path={ROOT_PATHS.MEMBER + '/:memberId'}
                element={<MemberInfo />}
              />

              <Route path={ROOT_PATHS.STAFF} element={<Staff />} />
              <Route
                path={ROOT_PATHS.STAFF + '/:staffId'}
                element={<StaffRouteElementWrapper />}
              />
              <Route path={ROOT_PATHS.ADD_STAFF} element={<AddStaff />} />

              <Route path={ROOT_PATHS.ADD_MEMBER} element={<AddMember />} />
              <Route
                path={ROOT_PATHS.START_BULK_IMPORT}
                element={<StartBulkImport />}
              />
              <Route
                path={`${ROOT_PATHS.REPORTS}/:reportId?`}
                element={<Reports />}
              />

              <Route
                path={ROOT_PATHS.TRAINING_TOPICS}
                element={<TrainingTopics />}
              />
              <Route
                path={ROOT_PATHS.TRAINING_TOPICS + '/:id'}
                element={<TrainingTopic />}
              />
              <Route
                path={ROOT_PATHS.ADD_TRAINING_TOPIC}
                element={<TrainingTopic isAdd />}
              />

              <Route path={ROOT_PATHS.FUNCTIONS} element={<FunctionsList />} />

              <Route
                path={ROOT_PATHS.TRAININGS}
                element={<TrainingsSession />}
              />
              <Route
                path={ROOT_PATHS.TRAININGS + '/:id'}
                element={<TrainingDetailsSession />}
              />
              <Route
                path={ROOT_PATHS.ADD_TRAINING}
                element={<TrainingDetailsSession isAdd />}
              />

              <Route
                path={ROOT_PATHS.BULK_UPLOAD_REQUEST}
                element={<BulkUploadRequest />}
              />
              <Route
                path={ROOT_PATHS.BULK_UPLOAD_REQUEST + '/:id'}
                element={<BulkUploadRequestDetails />}
              />

              <Route
                path={ROOT_PATHS.CERTIFICATE_TYPES}
                element={<CertificateTypes />}
              />
              <Route
                path={ROOT_PATHS.CERTIFICATE_TYPES + '/:id'}
                element={<CertificateTypesDetails />}
              />
              <Route
                path={ROOT_PATHS.ADD_CERTIFICATE_TYPE}
                element={<CertificateTypesDetails isAdd />}
              />

              <Route path={ROOT_PATHS.LOGS} element={<Logs />} />

              <Route
                path={ROOT_PATHS.ORGANISATION + '/:orgId/asset-types'}
                element={<OrgAssetTypes />}
              />
              <Route
                path={ROOT_PATHS.ORGANISATION + '/:orgId/payment-types'}
                element={<OrgPaymentTypes />}
              />

              <Route path={ROOT_PATHS.ON_HAND} element={<OnHand />} />
              <Route path={ROOT_PATHS.HISTORY} element={<History />} />
              <Route path={ROOT_PATHS.PROCESSING} element={<Processing />} />
              <Route
                path={ROOT_PATHS.UPLOAD_SECTION}
                element={<UploadSection />}
              />
              <Route
                path={ROOT_PATHS.TRANSACTIONS + '/:id'}
                element={<TransactionDetails />}
              />
              <Route
                path={ROOT_PATHS.BULK_PURCHASE}
                element={<BulkPurchase />}
              />
              <Route
                path={ROOT_PATHS.BULK_PURCHASE + '/:id'}
                element={<BulkPurchaseDetails />}
              />
              <Route
                path={ROOT_PATHS.TRANSACTION_REPORT + '/:id'}
                element={<TransactionReport />}
              />
              <Route
                path={ROOT_PATHS.PAYMENT_MANAGER}
                element={<PaymentManager />}
              />
              <Route
                path={ROOT_PATHS.ADD_BATCH}
                element={<BatchDetails isAdd />}
              />
              <Route
                path={ROOT_PATHS.BATCH + '/:id'}
                element={<BatchDetails />}
              />
              <Route path={ROOT_PATHS.E404} element={<PageNotFound />} />
              <Route
                path={ROOT_PATHS.SYSTEM_UNAVAILABLE}
                element={<SystemNotAvailable />}
              />
              <Route
                path='*'
                element={
                  <Navigate
                    to={
                      location.pathname.startsWith(ROOT_PATHS.LOGIN)
                        ? ROOT_PATHS.ROOT
                        : ROOT_PATHS.E404
                    }
                  />
                }
              />
            </Routes>
          </ConsentProvider>
        </AgErrorBoundary>
      </Layout>
    </Suspense>
  );
};

export default Router;
